import SearchIcon from '@mui/icons-material/Search';
import {
    Autocomplete,
    Box,
    InputAdornment,
    TextField,
    Typography,
    createFilterOptions,
} from '@mui/material';
import { Children, useMemo, useState } from 'react';

type SearchOption<T> = {
    row: T;
    recommended: boolean;
};

type RecommendationSearchProps<T> = {
    recommended: T[];
    notRecommended: T[];
    // Label shown for each option in the dropdown.
    getOptionLabel: (row: T) => string;
    // Text matched against the query. Defaults to `getOptionLabel`; callers can
    // widen it (e.g. to also match on id / external id).
    getSearchText?: (row: T) => string;
    // Stable identity for a row, used as the option key + equality check.
    getRowValue: (row: T) => unknown;
    onSelect: (row: T) => void;
    // Row values that cannot be selected (e.g. clinicians already assigned to
    // another slot). Disabled in the dropdown, mirroring the table.
    disabledRowValues?: Set<unknown>;
    recommendedLabel?: string;
    notRecommendedLabel?: string;
    placeholder?: string;
};

const byLabel =
    <T,>(getOptionLabel: (row: T) => string) =>
    (a: T, b: T) =>
        getOptionLabel(a).localeCompare(getOptionLabel(b), undefined, { sensitivity: 'base' });

/**
 * Autocomplete "jump to" search across a recommendation list. Spans both the
 * recommended and not-recommended groups: recommended options are listed first,
 * each group alphabetized, under a labelled header with a count. Selecting an
 * option fires `onSelect` and clears the input — it is a navigation control, not
 * a bound field, so it never holds a value of its own.
 */
export const RecommendationSearch = <T,>({
    recommended,
    notRecommended,
    getOptionLabel,
    getSearchText,
    getRowValue,
    onSelect,
    disabledRowValues,
    recommendedLabel = 'Recommended',
    notRecommendedLabel = 'Not Recommended',
    placeholder = 'Search by name, ID, or external ID…',
}: RecommendationSearchProps<T>) => {
    const [inputValue, setInputValue] = useState('');

    // Recommended first, then not-recommended; each group alphabetized. Ranking
    // is intentionally not part of the ordering — name search ignores scoring.
    const options = useMemo<SearchOption<T>[]>(() => {
        const sort = byLabel(getOptionLabel);
        return [
            ...recommended
                .slice()
                .sort(sort)
                .map((row) => ({ row, recommended: true })),
            ...notRecommended
                .slice()
                .sort(sort)
                .map((row) => ({ row, recommended: false })),
        ];
    }, [recommended, notRecommended, getOptionLabel]);

    // Case-insensitive partial (substring) matching against the search text.
    const filterOptions = useMemo(
        () =>
            createFilterOptions<SearchOption<T>>({
                stringify: (option) => (getSearchText ?? getOptionLabel)(option.row),
                trim: true,
            }),
        [getSearchText, getOptionLabel],
    );

    return (
        <Box sx={{ mb: 2 }}>
            <Autocomplete
                options={options}
                value={null}
                inputValue={inputValue}
                onInputChange={(_event, value, reason) => {
                    // Keep the typed text on input; clear on selection/blur.
                    setInputValue(reason === 'input' ? value : '');
                }}
                onChange={(_event, option) => {
                    if (option) {
                        onSelect(option.row);
                    }
                    setInputValue('');
                }}
                getOptionLabel={(option) => getOptionLabel(option.row)}
                getOptionDisabled={(option) =>
                    disabledRowValues?.has(getRowValue(option.row)) ?? false
                }
                isOptionEqualToValue={(a, b) => getRowValue(a.row) === getRowValue(b.row)}
                groupBy={(option) => (option.recommended ? recommendedLabel : notRecommendedLabel)}
                filterOptions={filterOptions}
                openOnFocus
                blurOnSelect
                clearOnBlur
                handleHomeEndKeys
                autoHighlight
                size='small'
                slotProps={{
                    paper: { sx: { borderRadius: '4px' } },
                }}
                ListboxProps={{ sx: { py: 0 } }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        placeholder={placeholder}
                        InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                                <InputAdornment position='start'>
                                    <SearchIcon fontSize='small' />
                                </InputAdornment>
                            ),
                        }}
                        inputProps={{
                            ...params.inputProps,
                            'data-testid': 'recommendation-search-input',
                        }}
                    />
                )}
                renderGroup={(params) => {
                    const isRecommended = params.group === recommendedLabel;
                    const count = Children.count(params.children);
                    return (
                        <li key={params.key}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    px: 2,
                                    pt: 1.5,
                                    pb: 0.5,
                                    position: 'sticky',
                                    top: 0,
                                    zIndex: 1,
                                    bgcolor: 'background.paper',
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        bgcolor: isRecommended ? 'primary.main' : 'grey.400',
                                    }}
                                />
                                <Typography
                                    variant='caption'
                                    sx={{
                                        fontWeight: 700,
                                        letterSpacing: '0.08em',
                                        textTransform: 'uppercase',
                                        color: isRecommended ? 'primary.main' : 'text.secondary',
                                    }}
                                >
                                    {params.group}
                                </Typography>
                                <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                                    {count}
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    borderLeft: isRecommended ? 2 : 0,
                                    borderColor: 'primary.main',
                                }}
                            >
                                {params.children}
                            </Box>
                        </li>
                    );
                }}
                renderOption={(props, option) => (
                    <Box component='li' {...props} key={String(getRowValue(option.row))}>
                        <Typography variant='body2' sx={{ fontWeight: 500 }}>
                            {getOptionLabel(option.row)}
                        </Typography>
                    </Box>
                )}
            />
        </Box>
    );
};
